// Service Worker Registration for ClinicNode PWA
// This module handles registering and managing the service worker lifecycle with proactive update detection

import { IS_TEST, IS_DEV } from './config';

const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(
        /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

// Store current version for comparison
let currentVersion = null;
let swRegistration = null;

/**
 * Register the service worker
 * @param {Object} config - Configuration object
 * @param {Function} config.onSuccess - Callback when SW is installed successfully
 * @param {Function} config.onUpdate - Callback when new content is available
 */
export function register(config = {}) {
    // Skip in test mode and development mode (for proper HMR support)
    if (IS_TEST || IS_DEV) {
        if (IS_DEV) {
            console.log('[SW] Service worker disabled in development mode for HMR support');
        }
        return;
    }

    if ('serviceWorker' in navigator) {
        window.addEventListener('load', () => {
            const swUrl = '/service-worker.js';

            if (isLocalhost) {
                checkValidServiceWorker(swUrl, config);
                navigator.serviceWorker.ready.then(() => {
                    console.log(
                        'This web app is being served cache-first by a service worker.'
                    );
                });
            } else {
                registerValidSW(swUrl, config);
            }

            // Start proactive version checking
            startVersionChecking(config);
        });
    }
}

function registerValidSW(swUrl, config) {
    navigator.serviceWorker
        .register(swUrl)
        .then((registration) => {
            console.log('SW registered:', registration);
            swRegistration = registration;

            // Check for updates more frequently (every 5 minutes instead of hourly)
            setInterval(() => {
                registration.update();
            }, 5 * 60 * 1000); // Check every 5 minutes

            registration.onupdatefound = () => {
                const installingWorker = registration.installing;
                if (installingWorker == null) {
                    return;
                }

                installingWorker.onstatechange = () => {
                    if (installingWorker.state === 'installed') {
                        if (navigator.serviceWorker.controller) {
                            console.log('New content is available; please refresh.');
                            if (config && config.onUpdate) {
                                config.onUpdate(registration);
                            }
                        } else {
                            console.log('Content is cached for offline use.');
                            if (config && config.onSuccess) {
                                config.onSuccess(registration);
                            }
                        }
                    }
                };
            };
        })
        .catch((error) => {
            console.error('Error during service worker registration:', error);
        });
}

function checkValidServiceWorker(swUrl, config) {
    fetch(swUrl, {
        headers: { 'Service-Worker': 'script' },
    })
        .then((response) => {
            const contentType = response.headers.get('content-type');
            if (
                response.status === 404 ||
                (contentType != null && contentType.indexOf('javascript') === -1)
            ) {
                navigator.serviceWorker.ready.then((registration) => {
                    registration.unregister().then(() => {
                        window.location.reload();
                    });
                });
            } else {
                registerValidSW(swUrl, config);
            }
        })
        .catch(() => {
            console.log('No internet connection found. App is running in offline mode.');
        });
}

/**
 * Start proactive version checking
 * Checks version.json on visibility change and periodically (throttled)
 */
let lastVersionCheck = 0;
const VERSION_CHECK_INTERVAL = 30000; // Minimum 30 seconds between checks

function startVersionChecking(config) {
    // Fetch initial version
    fetchVersion().then(version => {
        currentVersion = version;
        if (version) {
            console.log('[Version] Initial version:', currentVersion);
        }
    });

    // Throttled check function
    const throttledCheck = () => {
        const now = Date.now();
        if (now - lastVersionCheck < VERSION_CHECK_INTERVAL) {
            return; // Skip if checked recently
        }
        lastVersionCheck = now;
        checkForNewVersion(config);
    };

    // Check version when app becomes visible (user returns to tab/app)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            throttledCheck();
        }
    });

    // Also check when window gets focus (covers PWA and browser)
    window.addEventListener('focus', throttledCheck);
}

/**
 * Fetch current version from server
 */
async function fetchVersion() {
    try {
        const response = await fetch('/version.json', {
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache'
            }
        });

        // Check if response is OK and is JSON
        if (!response.ok) {
            // 404 or other error - silently ignore
            return null;
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            // Not JSON - silently ignore (likely got index.html fallback)
            console.log('[Version] Response is not JSON, skipping');
            return null;
        }

        const data = await response.json();
        return data.cacheKey;
    } catch (e) {
        // Silently fail - version checking is non-critical
        console.log('[Version] Could not fetch version:', e.message);
    }
    return null;
}

/**
 * Check if a new version is available
 */
async function checkForNewVersion(config) {
    const newVersion = await fetchVersion();

    if (newVersion && currentVersion && newVersion !== currentVersion) {
        console.log('[Version] New version detected:', newVersion, 'Current:', currentVersion);

        // Trigger SW update check
        if (swRegistration) {
            try {
                await swRegistration.update();
            } catch (err) {
                // Ignore update errors (network offline, etc) to prevent app crash
                console.log('[Version] SW update check failed (non-critical):', err.message);
            }
        }

        // Notify the app about the update
        if (config && config.onUpdate) {
            config.onUpdate(swRegistration);
        }

        // Update stored version
        currentVersion = newVersion;
    }
}

/**
 * Unregister the service worker
 */
export function unregister() {
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready
            .then((registration) => {
                registration.unregister();
            })
            .catch((error) => {
                console.error(error.message);
            });
    }
}

/**
 * Check if app is installed as PWA
 * @returns {boolean}
 */
export function isInstalled() {
    return window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
}

/**
 * Skip waiting and activate new service worker
 * @param {ServiceWorkerRegistration} registration
 */
export function skipWaiting(registration) {
    if (registration && registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
}

/**
 * Force reload the page to get new content
 * This bypasses the cache and gets fresh content
 */
export function forceRefresh() {
    // Clear caches before reload for cleanest update
    if ('caches' in window) {
        caches.keys().then(names => {
            names.forEach(name => {
                caches.delete(name);
            });
        });
    }

    // Reload with cache bypass
    window.location.reload();
}
